<!-- TreeNav.vue -->
<template>
  <ul class="nav-tree">
    <li
      v-for="node in nodes"
      :key="node.id"
      :class="{ active: store.activeNodeId === node.id }"
    >
      <div class="node-item" :class="`level-${node.level}`">
        <a :href="`#${node.numId}`" @click.prevent="scrollToNode(node)">
          {{ node.numId }} {{ node.name }}
        </a>
        <button
          v-if="node.hasChildren"
          @click.stop="store.toggleNode(node.id)"
          class="toggle-btn"
        >
          {{ store.expandedNodes.has(node.id) ? "−" : "+" }}
        </button>
      </div>
      <TreeNav v-if="store.expandedNodes.has(node.id)" :nodes="node.children" />
    </li>
  </ul>
</template>

<script setup lang="ts">
import { useStandardStore } from "@/stores/standard";
import { defineProps } from "vue";
import { getStandardById } from "@/api/menu";
import { ref } from "vue";

const standardNodes = ref([]);

// TreeNav.vue 修改后的数据处理方法
const processLevelWithDuplicateCheck = (
  levelData: any[],
  parentLevel: number = 1,
  parentNumId: string = ""
) => {
  const uniqueChildren: Record<string, boolean> = {};

  return levelData
    .map((level) => {
      // 严格匹配父子层级关系
      if (parentNumId) {
        const parentPrefix = `${parentNumId}.`;
        // 精确匹配父级前缀 (如 "1." 防止匹配到 "11.")
        if (!level.numId?.startsWith(parentPrefix)) {
          return null;
        }
      }

      // 验证当前层级是否合法
      const currentLevel = level.numId?.split(".").length || 1;
      if (currentLevel !== parentLevel + 1) {
        return null;
      }

      // 去重处理
      if (uniqueChildren[level.numId]) {
        return null;
      }
      uniqueChildren[level.numId] = true;

      // 递归处理子节点
      const children = processLevelWithDuplicateCheck(
        level[`level${currentLevel + 1}`] || [],
        currentLevel,
        level.numId
      );

      return {
        id: level.id,
        numId: level.numId,
        level: currentLevel,
        name: level.name,
        content: level.content,
        image: level.image,
        hasChildren: children.length > 0,
        children: children,
      };
    })
    .filter(Boolean);
};

interface StandardNode {
  id: number;
  numId: string;
  level: number;
  name: string;
  content?: string;
  image?: string;
  hasChildren: boolean;
  children: StandardNode[];
}

const props = defineProps<{
  nodes: StandardNode[];
}>();

const store = useStandardStore();

const scrollToNode = (node: StandardNode) => {
  const target = document.getElementById(node.numId);
  if (target) {
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    store.setActiveNode(node.id);
  }
};
</script>

<style scoped>
.nav-tree {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.nav-tree li {
  position: relative;
  margin: 10px 0;
}

.node-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  border-radius: 5px;
  /* transition: background-color 0.3s; */
  cursor: pointer;
}

.node-item:hover {
  background-color: #f0f7f0; /* Light hover effect */
}

.node-item.active {
  background-color: #d0e8d0; /* Active item background */
  font-weight: bold;
}

.toggle-btn {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: #333;
  /* transition: color 0.3s; */
}

.toggle-btn:hover {
  color: #007bff; /* Change color on hover */
}

.level-1 {
  font-weight: bold;
  font-size: 1.2em;
}

.level-2 {
  padding-left: 20px;
}

.level-3 {
  padding-left: 40px;
}

.level-4 {
  padding-left: 60px;
}
/* TreeNav.vue 样式优化 */
.nav-tree {
  --level-indent: 20px;
  --active-color: #2b542c;
  --hover-bg: #f0f7f0;
}

.node-item {
  position: relative;
  padding: 8px 15px;
  /* transition: all 0.2s; */
  border-radius: 6px;

  :is(a) {
    color: #444;
    text-decoration: none;
    flex-grow: 1;

    &:hover {
      color: var(--active-color);
    }
  }

  &.active {
    background: var(--hover-bg);
    border-left: 3px solid var(--active-color);
    :is(a) {
      color: var(--active-color);
    }
  }

  &:hover {
    background: var(--hover-bg);
  }
}

/* 层级缩进 */
[class^="level-"] {
  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    height: 2px;
    background: #ddd;
  }
}

.level-1 {
  padding-left: calc(var(--level-indent) * 0);
}
.level-2 {
  padding-left: calc(var(--level-indent) * 1);
  &::before {
    width: 10px;
  }
}
.level-3 {
  padding-left: calc(var(--level-indent) * 2);
  &::before {
    width: 20px;
  }
}
.level-4 {
  padding-left: calc(var(--level-indent) * 3);
  &::before {
    width: 30px;
  }
}

.toggle-btn {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: #e8f5e9;
  color: var(--active-color);
  /* transition: all 0.2s; */

  &:hover {
    background: var(--active-color);
    color: white;
  }
}
</style>
